import { localDateString } from './calendarDates';

export function entryInterval(entry) {
  if (entry.planning_status === 'cancelled') return null;
  let start = Date.parse(entry.start_at), end = Date.parse(entry.end_at);
  // Existing calendar imports stored local start/end times in notes.
  if (!Number.isFinite(start)) {
    const clock = /^(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/.exec(entry.notes || '');
    if (!clock || !/^\d{4}-\d{2}-\d{2}$/.test(entry.date || '')) return null;
    start = new Date(`${entry.date}T${clock[1]}`).getTime();
    end = start + Number(entry.duration_minutes) * 60000;
  }
  return Number.isFinite(start) && end > start ? { start, end } : null;
}

export function scheduleFocusTasks(tasks, entries, { startDate, days = [1, 2, 3, 4, 5], startHour = '09:00', endHour = '17:00', dailyMinutes = 120, now = Date.now() }) {
  const clock = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate || '') || !clock.test(startHour) || !clock.test(endHour) || startHour >= endHour || !Array.isArray(days) || !days.length || days.some(day => !Number.isInteger(day) || day < 0 || day > 6) || !Number.isFinite(Number(dailyMinutes)) || dailyMinutes < 15 || dailyMinutes > 480) throw new Error('Choose working days, valid hours and a daily focus budget.');
  const first = new Date(`${startDate}T00:00:00`);
  if (localDateString(first) !== startDate) throw new Error('Choose a valid start date.');
  const occupied = entries.map(entryInterval).filter(Boolean);
  const plannedMinutes = new Map();
  for (const entry of entries.filter(row => row.planning_status === 'scheduled')) plannedMinutes.set(entry.date, (plannedMinutes.get(entry.date) || 0) + Number(entry.duration_minutes || 0));
  return tasks.map(task => {
    if (!Number.isInteger(task.duration_minutes) || task.duration_minutes < 15 || task.duration_minutes > 120) throw new Error('The suggested focus duration is invalid. Generate a new plan.');
    const duration = task.duration_minutes * 60000;
    for (let offset = 0; offset < 7; offset++) {
      const day = new Date(first); day.setDate(day.getDate() + offset);
      if (!days.includes(day.getDay())) continue;
      const date = localDateString(day);
      if ((plannedMinutes.get(date) || 0) + task.duration_minutes > dailyMinutes) continue;
      const end = new Date(`${date}T${endHour}`).getTime();
      let start = Math.max(new Date(`${date}T${startHour}`).getTime(), Math.ceil((now + 15 * 60000) / (15 * 60000)) * 15 * 60000);
      while (start + duration <= end) {
        const candidate = start;
        const conflict = occupied.find(range => candidate < range.end && candidate + duration > range.start);
        if (conflict) { start = Math.ceil(conflict.end / (15 * 60000)) * 15 * 60000; continue; }
        occupied.push({ start, end: start + duration });
        plannedMinutes.set(date, (plannedMinutes.get(date) || 0) + task.duration_minutes);
        return { ...task, start_at: new Date(start).toISOString(), end_at: new Date(start + duration).toISOString(), date };
      }
    }
    return { ...task, start_at: null, end_at: null, date: null };
  });
}

const icsText = value => String(value || '').replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r/g, '');
const icsTime = date => new Date(date).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
export function focusCalendarFile(block) {
  const start = Date.parse(block.start_at), end = Date.parse(block.end_at);
  if (!Number.isFinite(start) || end <= start) throw new Error('Choose a valid calendar time.');
  const minutes = Number(block.reminder_minutes_before ?? 15);
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) throw new Error('Choose a valid reminder lead time.');
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//SPCC//Focus planning//EN', 'CALSCALE:GREGORIAN', 'BEGIN:VEVENT',
    `UID:${icsText(block.id)}@spcc`, `DTSTAMP:${icsTime(new Date())}`, `DTSTART:${icsTime(start)}`, `DTEND:${icsTime(end)}`,
    `SUMMARY:${icsText(block.activity_title || block.title)}`, `DESCRIPTION:${icsText(block.notes || block.reason)}`, 'TRANSP:OPAQUE',
    'BEGIN:VALARM', 'ACTION:DISPLAY', `TRIGGER:-PT${minutes}M`, `DESCRIPTION:${icsText(block.activity_title || block.title)}`, 'END:VALARM', 'END:VEVENT', 'END:VCALENDAR'];
  // RFC 5545 folds content lines at 75 octets, preserving multi-byte text.
  return lines.map(line => {
    let result = '', size = 0;
    for (const char of line) { const bytes = new TextEncoder().encode(char).length; if (size + bytes > 75) { result += '\r\n '; size = 1; } result += char; size += bytes; }
    return result;
  }).join('\r\n') + '\r\n';
}

export function downloadFocusCalendar(block) {
  const url = URL.createObjectURL(new Blob([focusCalendarFile(block)], { type: 'text/calendar;charset=utf-8' }));
  const link = document.createElement('a'); link.href = url; link.download = 'spcc-focus.ics'; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
